import {Vue, Component} from "vue-property-decorator";
import Toaster from "@/components/Toaster.vue";
import {ToasterConfigUpdate, ToasterType} from "@/types/ToasterConfig";
import {AxiosError, AxiosResponse} from "axios";
import api from "@/services/apiService";
import {ErrorService} from "@/services/ErrorService";

enum LoginSteps {
  Email = 'email',
  Password = 'password',
  Numendo = 'numendo',
  Forgot = 'forgot',
}

@Component({
  components: {
    Toaster,
  },
})
export default class Login extends Vue {

  public currentStep: LoginSteps = LoginSteps.Email;

  public typePassword: string = 'password';
  public eyesImages = {
    eyeOpen: require('@/assets/eye-fill.svg'),
    eyeClose: require('@/assets/eye-off.svg'),
  };
  public passwordLogo: string = this.eyesImages.eyeClose;
  public forgetPassword = {
    passwordType: 'password' as string,
    passwordLogo: this.eyesImages.eyeClose as string,
    passwordConfirmationType: 'password' as string,
    passwordConfirmationLogo: this.eyesImages.eyeClose as string,
  };

  // ******************** FIN CONFIG ********************

  public email: string = "";
  public password: string = "";
  public token: string = "";
  public newPassword: string = "";
  public newPasswordConfirmation: string = "";
  public loginError: string = "";
  public isButtonBlocked: boolean = true;
  public isLoading: boolean = false;

  public minimumChar: number = 1;

  public errorMessages = {
    email: "",
  };

  public emailErrorInterval: number = 0;

  mounted() {
    if (this.$route.params.token) {
      this.token = this.$route.params.token;
      this.changeStep(LoginSteps.Forgot);
    }
  }

  public formValidated() {
    switch (this.currentStep) {
      case LoginSteps.Email:
        this.verifyEmail();
        break;
      case LoginSteps.Password:
        this.authenticate();
        break;
      case LoginSteps.Forgot:
        this.saveNewPassword();
        break;
    }
  }

  public changeStep(next: LoginSteps) {
    this.isButtonBlocked = true;
    this.password = '';
    this.currentStep = next;

    if (next === LoginSteps.Email) {
      setTimeout(() => {
        this.validateEmail();
        const passwordInput = this.$refs.emailInput as HTMLInputElement
        passwordInput.focus();
      }, 0);
    }

    if (next === LoginSteps.Password) {
      setTimeout(() => {
        this.validatePassword();
        const passwordInput = this.$refs.passwordInput as HTMLInputElement
        passwordInput.focus();
      }, 0);
    }
  }

  public verifyEmail() {

    let isValid = this.validateEmail();
    if (!isValid) return;

    const regexNumendo = /(@numendo.com)$/;
    //const isNumendo: boolean = regexNumendo.test(this.email);
    const isNumendo = false; // en attendant la connexion avec google pour les employés de Numendo
    if (isNumendo) {
      this.changeStep(LoginSteps.Numendo);
    } else {
      this.changeStep(LoginSteps.Password);
    }
  }

  public validateEmail() {
    clearTimeout(this.emailErrorInterval);
    this.errorMessages.email = "";
    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const regexResult = regex.test(this.email.toLowerCase());
    if (this.email === "") {
      this.isButtonBlocked = true;
      this.emailErrorInterval = setTimeout(() => {
        this.errorMessages.email = "Veuillez saisir un email.";
      }, 750);
      return false;
    } else if (!regexResult) {
      this.isButtonBlocked = true;
      this.emailErrorInterval = setTimeout(() => {
        this.errorMessages.email = "Veuillez saisir un email valide.";
      }, 750);
      return false;
    }
    this.isButtonBlocked = false;
    return true;
  }

  public validatePassword() {
    this.isButtonBlocked = true;
    let isValid = false;
    if (this.currentStep === LoginSteps.Password) {
      isValid = this.password.length >= this.minimumChar;
    } else if (this.currentStep === LoginSteps.Forgot) {
      isValid = this.newPassword.length >= this.minimumChar && this.newPasswordConfirmation.length >= this.minimumChar && this.newPassword === this.newPasswordConfirmation;
    }

    if (isValid) this.isButtonBlocked = false;
    return isValid;
  }

  public togglePasswordVisibility(element: 'mainPassword' | 'forgetPassword' | 'forgetPasswordConfirmation') {

    const toggle = (typePassword: string) => {
      if (typePassword === 'password') {
        return {
          typePassword: 'text',
          passwordLogo: this.eyesImages.eyeOpen,
        };
      } else {
        return {
          typePassword: 'password',
          passwordLogo: this.eyesImages.eyeClose,
        };
      }
    }

    if (element === 'mainPassword') {
      const response = toggle(this.typePassword);
      this.typePassword = response.typePassword;
      this.passwordLogo = response.passwordLogo;
    } else if (element === 'forgetPassword') {
      const response = toggle(this.forgetPassword.passwordType);
      this.forgetPassword.passwordType = response.typePassword;
      this.forgetPassword.passwordLogo = response.passwordLogo;
    } else if (element === 'forgetPasswordConfirmation') {
      const response = toggle(this.forgetPassword.passwordConfirmationType);
      this.forgetPassword.passwordConfirmationType = response.typePassword;
      this.forgetPassword.passwordConfirmationLogo = response.passwordLogo;
    }
  }

  public resetPassword() {
    api.authRequest.post('/login/forgotten-password', {
      email: this.email,
    }).then((response) => {
      this.$store.commit('updateToaster', {
        type: ToasterType.Green,
        message: 'Un email vous sera envoyé pour vous permettre de changer votre mot de passe.',
      });
    }).catch((error) => {
      ErrorService.dealWithTheError(error);
    }).finally(() => {
      this.changeStep(LoginSteps.Password);
    });

  }

  public async authenticate() {
    this.isLoading = true;
    await api.apiRequest.post('login', {
      "email": this.email,
      "password": this.password,
    }).then((response: AxiosResponse) => {
      this.$store.commit('login', response.data);
      this.$router.push({name: 'Home'});
    }).catch((error: any) => {
      ErrorService.dealWithTheError(error);
    }).finally(() => {
      this.isButtonBlocked = false;
      this.isLoading = false;
    });
  }

  public saveNewPassword() {
    api.authRequest.post('/login/reset-password', {
      token: this.token,
      newPassword: this.newPassword,
      newPasswordConfirmation: this.newPasswordConfirmation,
    }).then((response: AxiosResponse) => {
      this.$store.commit('updateToaster', {
        type: ToasterType.Green,
        message: 'Votre mot de passe a bien été modifié.',
        isVisible: false,
        visibleOnNextPage: true,
      });
      this.$router.push({name: 'Login'});
      this.isButtonBlocked = true;
      this.currentStep = LoginSteps.Email;
    }).catch((error) => {
      ErrorService.dealWithTheError(error);
    });
  }

}
